import React, { FunctionComponent } from 'react';
import { startCase } from 'lodash';
import { TradeInType } from '@upstart/harmony/lib/constants';
import vautoImage from '../../../assets/guides/tradein-tool-vauto.png';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { Select } from '../../Input/Select/Select';
import { TextField } from '../../Input/TextField/TextField';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import {
  addDefaultTradeInManager,
  addTradeInManagerDefaults,
} from '../defaults';
import { StepInfoGateProps } from './DealerLaunchInfoGate';
import { TRADEIN_MANAGER_TYPE_NAME } from '../../../utils/constants';
import { buildModalBreadcrumbs, QuickGuide } from '../../QuickGuide/QuickGuide';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';

export const DealerLaunchIntegrationTradeinManagerInfoGate: StepInfoGateProps = {
  title: "Now, Let's Integrate with your Trade-in System",
  cardTitle: 'A quick note before we connect with your Trade-in System',
  cardDescription:
    'Throughout this process, we will need access to your Trade In System. This will help us build you a seamless car-selling experience by pushing trade-ins automatically to your Trade In system.',
  continueText: 'I Understand',
};

export const DealerLaunchIntegrationTradeinManager: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({
    defaultValues: addDefaultTradeInManager(dealer),
  });
  const tradeInManagerType = form.watch('tradeInManagerType');
  const options: Array<{ label: string; value: string }> = Object.values(
    TradeInType,
  ).map((type) => ({
    label: TRADEIN_MANAGER_TYPE_NAME[type] || startCase(type),
    value: type,
  }));

  options.unshift({ label: 'None', value: '' });

  const onSubmit = (data: Dealer) => {
    return nextStep(
      addTradeInManagerDefaults({
        ...data,
        launchpad: {
          status: {
            finishedTradeInManagerSelection: true,
          },
        },
      }),
    );
  };

  return (
    <DealerLaunchFormCardLayout
      className={styles.homeStep}
      form={form}
      title="Now, Let's Integrate with your Trade-in System"
      subTitle="To seamlessly integrate with your business, Upstart Auto Retail pushes trade-ins to your trade-in tool. To do this, we will need access to your trade-in system."
      cardTitle="Trade-in System"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <div className={styles.stepRow}>
        <Select
          label="Provider"
          name="tradeInManagerType"
          width="md"
          form={form}
          validation={{ required: false }}
          options={options}
        />
      </div>
      {tradeInManagerType === TradeInType.VAUTO && (
        <>
          <div className={styles.stepRow}>
            <TextField
              label="Web Login Username"
              name="vauto.login.username"
              width="md"
              form={form}
              validation={{ required: true }}
            />
            <TextField
              label="Web Login Password"
              name="vauto.login.password"
              type="password"
              width="md"
              form={form}
              validation={{ required: true }}
            />
          </div>
          <QuickGuide
            label="Not sure how to setup a new admin user in your Trade-in Tool?"
            action="Help Me Create a New Admin Account"
            guideTitle="Trade-in Tool Account Creation"
          >
            Once logged in to Provision, select
            {buildModalBreadcrumbs(
              'Settings ⚙',
              'User Management',
              'Create New User',
            )}
            Select a unique user ID and password
            <br />
            <br />
            Fill in first name &quot;Get&quot;, last name &quot;Upstart Auto
            Retail&quot;, and email address &quot;dealersetup@upstart.com&quot;.
            <br />
            <br /> Uncheck &quot;Force Password Expiration&quot; and under
            permissions select &quot;System Administrator&quot;,
            &quot;Salesperson&quot;, and &quot;Finalize Appraisals&quot;.
            <img
              className="black-border"
              src={vautoImage}
              alt="VAuto User Roles"
            />
          </QuickGuide>
        </>
      )}
    </DealerLaunchFormCardLayout>
  );
};
